import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EUR } from "@/lib/format";
import { History, Search, Filter, User, Clock, TrendingUp, AlertCircle, Trash2 } from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}
function dayKey(iso) {
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return iso; }
}

export default function DonationsAudit() {
  const [search, setSearch] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [userFilter, setUserFilter] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const qc = useQueryClient();

  const params = useMemo(() => {
    const p = { limit: 5000 };
    if (since) p.since = since;
    if (until) p.until = until;
    if (userFilter.length) p.user_id = userFilter.join(",");
    return p;
  }, [since, until, userFilter]);

  const toggleUser = (id) => { setUserFilter((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id])); setSel(new Set()); };
  const toggleIds = (ids, on) => setSel((s) => { const n = new Set(s); ids.forEach((i) => (on ? n.add(i) : n.delete(i))); return n; });

  const delMut = useMutation({
    mutationFn: async () => (await api.post("/payments/bulk-delete", { ids: [...sel] })).data,
    onSuccess: (r) => {
      toast.success(`${r.deleted} donazioni eliminate`);
      setSel(new Set()); setConfirmOpen(false);
      ["donations-audit", "payments", "donors", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["donations-audit", params],
    queryFn: async () => (await api.get("/payments/audit-log", { params })).data,
  });

  const filtered = useMemo(() => {
    const items = data?.items || [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      p.donor_name?.toLowerCase().includes(q) ||
      p.reference?.toLowerCase().includes(q) ||
      p.receipt_number?.toLowerCase().includes(q) ||
      p.notes?.toLowerCase().includes(q) ||
      String(p.amount).includes(q)
    );
  }, [data, search]);

  // raggruppa per giorno di inserimento
  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of filtered) {
      const k = dayKey(p.created_at);
      if (!map.has(k)) map.set(k, { day: k, items: [], total: 0 });
      const g = map.get(k);
      g.items.push(p);
      g.total += p.amount || 0;
    }
    return Array.from(map.values()).sort((a, b) => b.day.localeCompare(a.day));
  }, [filtered]);

  const totalAmount = useMemo(() => filtered.reduce((s, p) => s + (p.amount || 0), 0), [filtered]);
  const selAmount = useMemo(() => filtered.filter((p) => sel.has(p.id)).reduce((s, p) => s + (p.amount || 0), 0), [filtered, sel]);
  const allSel = filtered.length > 0 && filtered.every((p) => sel.has(p.id));

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Cronologia inserimenti donazioni"
        subtitle="Ordine di inserimento in DB con data + orario. Utile per rintracciare quando è stato aggiunto un importo sospetto."
      />

      {/* Filtri */}
      <Card className="p-4 border border-stone-200 rounded-xl shadow-none mb-5" data-testid="audit-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              data-testid="audit-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca donatore, ricevuta, importo, nota…"
              className="pl-9"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">Inserimento dal</label>
            <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="audit-since"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">al</label>
            <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="audit-until"/>
          </div>
        </div>
      </Card>

      {/* KPI riepilogo */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><Clock size={11}/> Inserimenti mostrati</div>
            <div className="text-2xl font-semibold mt-1">{filtered.length}</div>
          </Card>
          <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><TrendingUp size={11}/> Totale importi</div>
            <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{EUR.format(totalAmount)}</div>
          </Card>
          <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><User size={11}/> Utenti che hanno inserito</div>
            <div className="text-2xl font-semibold mt-1">{(data.by_user || []).length}</div>
          </Card>
          <Card className="p-4 border border-amber-200 bg-amber-50/60 rounded-xl shadow-none">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1"><AlertCircle size={11}/> Suggerimento</div>
            <div className="text-[11px] text-amber-800 mt-1 leading-snug">
              Filtra per data d'inserimento sospetta e ordina per importo per trovare la donazione da €100 in eccesso.
            </div>
          </Card>
        </div>
      )}

      {/* Riepilogo per utente */}
      {data?.by_user?.length > 0 && (
        <Card className="p-4 border border-stone-200 rounded-xl shadow-none mb-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Chi ha inserito cosa — seleziona una o più persone</div>
          <div className="flex flex-wrap gap-2">
            {data.by_user.map((u) => (
              <button
                key={u.user_id}
                data-testid={`audit-user-${u.user_id}`}
                onClick={() => toggleUser(u.user_id)}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs border transition-colors ${
                  userFilter.includes(u.user_id)
                    ? "bg-[#1C352D] text-white border-[#1C352D]"
                    : "bg-white border-stone-200 hover:border-stone-400"
                }`}
              >
                <User size={11}/> {u.name} · {u.count} · <span className="tabular-nums">{EUR.format(u.total)}</span>
              </button>
            ))}
            {userFilter.length > 0 && (
              <button onClick={() => { setUserFilter([]); setSel(new Set()); }} className="text-xs text-stone-500 hover:text-stone-700 underline" data-testid="audit-user-clear">
                Rimuovi filtro utenti
              </button>
            )}
          </div>
        </Card>
      )}

      {filtered.length > 0 && (
        <Card className="p-3 border border-stone-200 rounded-xl shadow-none mb-5 flex flex-wrap items-center gap-3 sticky top-2 z-10 bg-white/95 backdrop-blur" data-testid="audit-bulk-bar">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={allSel} onCheckedChange={(v) => toggleIds(filtered.map((p) => p.id), !!v)} data-testid="audit-select-all"/>
            Seleziona tutti i {filtered.length} mostrati
          </label>
          <div className="flex-1 text-sm text-stone-600" data-testid="audit-selected-count">
            {sel.size > 0 ? <>Selezionati <b>{sel.size}</b> · {EUR.format(selAmount)}</> : "Nessuna selezione"}
          </div>
          {sel.size > 0 && <Button variant="ghost" size="sm" onClick={() => setSel(new Set())}>Deseleziona</Button>}
          <Button size="sm" disabled={!sel.size} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="audit-bulk-delete">
            <Trash2 size={13} className="mr-1"/> Elimina selezionati
          </Button>
        </Card>
      )}

      {/* Timeline raggruppata per giorno d'inserimento */}
      {isLoading ? (
        <div className="py-16 text-center text-stone-400 text-sm">Caricamento cronologia…</div>
      ) : grouped.length === 0 ? (
        <Card className="p-10 text-center border border-stone-200 rounded-xl shadow-none">
          <History size={32} className="mx-auto text-stone-300 mb-2"/>
          <div className="text-sm text-stone-500">Nessuna donazione trovata coi filtri correnti.</div>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={g.day} data-testid={`audit-day-${g.day}`}>
              <div className="flex items-baseline gap-3 mb-2">
                <Checkbox className="self-center" checked={g.items.every((p) => sel.has(p.id))} onCheckedChange={(v) => toggleIds(g.items.map((p) => p.id), !!v)} data-testid={`audit-select-day-${g.day}`}/>
                <div className="text-sm font-semibold capitalize">{fmtDateShort(g.day)}</div>
                <Badge variant="outline" className="text-[10px] rounded-full">
                  {g.items.length} inserimenti · {EUR.format(g.total)}
                </Badge>
              </div>
              <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50/60 text-[10px] uppercase tracking-widest text-stone-500">
                      <tr>
                        <th className="w-8 pl-4 py-2"></th>
                        <th className="text-left px-4 py-2 font-medium w-[130px]">Ora inserim.</th>
                        <th className="text-left px-3 py-2 font-medium">Donatore</th>
                        <th className="text-left px-3 py-2 font-medium">Data donaz.</th>
                        <th className="text-right px-3 py-2 font-medium">Importo</th>
                        <th className="text-left px-3 py-2 font-medium">Metodo</th>
                        <th className="text-left px-3 py-2 font-medium">Ricevuta</th>
                        <th className="text-left px-3 py-2 font-medium">Inserito da</th>
                        <th className="text-left px-4 py-2 font-medium">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {g.items.map((p) => (
                        <tr key={p.id} data-testid={`audit-row-${p.id}`} className={sel.has(p.id) ? "bg-red-50/60" : "hover:bg-stone-50"}>
                          <td className="pl-4 py-2"><Checkbox checked={sel.has(p.id)} onCheckedChange={(v) => toggleIds([p.id], !!v)} data-testid={`audit-select-${p.id}`}/></td>
                          <td className="px-4 py-2 tabular-nums font-mono text-[11px] text-stone-600">
                            {new Date(p.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                          </td>
                          <td className="px-3 py-2">
                            <Link to={`/donatori/${p.donor_id}`} className="hover:underline font-medium">
                              {p.donor_name}
                            </Link>
                            {p.silent && <span className="ml-1.5 text-[9px] uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">silent</span>}
                          </td>
                          <td className="px-3 py-2 text-stone-600 text-[13px]">{p.payment_date}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">{EUR.format(p.amount)}</td>
                          <td className="px-3 py-2 text-stone-600 capitalize text-[13px]">{p.method || "—"}</td>
                          <td className="px-3 py-2 text-stone-500 font-mono text-[11px]">{p.receipt_number || p.reference || "—"}</td>
                          <td className="px-3 py-2 text-stone-600 text-[13px]">{p.created_by_name || <span className="text-stone-300 italic">sconosciuto</span>}</td>
                          <td className="px-4 py-2 text-stone-500 text-[12px] max-w-[240px] truncate" title={p.notes || ""}>
                            {p.notes || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {sel.size} donazioni?</AlertDialogTitle>
            <AlertDialogDescription>
              Totale {EUR.format(selAmount)}. Le donazioni verranno cancellate definitivamente; le eventuali scadenze collegate torneranno "in attesa".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); delMut.mutate(); }} disabled={delMut.isPending} className="bg-red-600 hover:bg-red-700" data-testid="audit-bulk-confirm">
              {delMut.isPending ? "Eliminazione…" : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
